import { Box, Button, Chip, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getTranslationBasedOnLanguage } from 'src/utils/get-translation-based-on-language';

import { Query } from '../../../../components/atoms/query';
import { Adjustments as AdjustmentsIcon } from '../../../../icons/adjustments';

import { VisitorsFilters } from './visitors-filters';

import { formatDate } from '@/components/atoms/format-date';
import { dateFormats } from '@/constants/date-formats';
import { useDestinations } from '@/features/destinations/hooks/query/use-destinations';
import { OrganizationLanguage } from '@/features/settings/api/types/gdpr';
import { VisitSource } from '@/features/visitors/api/types/visitor';
import { VisitorState, VisitState } from '@/features/visits/api/types/visits';
import { useCategories } from '@/features/visits/hooks/query/use-categories';
import { FilterOperator } from '@/hooks/meilisearch/types/filter-value';
import { Filters } from '@/types/interfaces/filters';

interface Props {
  disabled: boolean;
  filters: Filters;
  handleChangeFilterValue: (
    facetType: string,
    val: string | string[] | number[],
    filterOperator?: FilterOperator | undefined,
  ) => void;
  query: string;
  resetFilters: () => void;
  handleChangeQuery: (query: string) => void;
  resetSingleFilter: (filterKey: string) => void;
}

const VisitorsSearchAndFilters = ({
  disabled,
  filters,
  handleChangeFilterValue,
  query,
  handleChangeQuery,
  resetSingleFilter,
  resetFilters,
}: Props) => {
  const { t, i18n } = useTranslation(['visits', 'common']);

  const [openFilters, setOpenFilters] = useState(false);

  const { data: categoryData } = useCategories();

  // WE ARE FETCHING HERE ONLY ONE DESTINATION TO GET TOTAL COUNT OF DESTINATIONS
  const { data: destinationData } = useDestinations({ page: 1, perPage: 1 });

  const { data: destinationsData } = useDestinations({
    page: 1,
    perPage: destinationData?.total || 10,
    enabled: !!destinationData,
  });

  const onDeselectFilterValue = (
    filterType: string,
    value: string,
    singleOption: boolean = false,
    filterOperator?: FilterOperator | undefined,
  ) => {
    let selectedCopy = [...((filters[filterType]?.value as string[]) ?? [])];

    if (singleOption) {
      if (selectedCopy.includes(value)) {
        selectedCopy = [];
      } else {
        selectedCopy = [value];
      }
    } else {
      if (selectedCopy.includes(value)) {
        selectedCopy = selectedCopy.filter(item => item !== value);
      } else {
        selectedCopy.push(value);
      }
    }

    handleChangeFilterValue(filterType, selectedCopy, filterOperator);
  };

  const renderFilterChips = () =>
    Object.entries(filters)?.map(([filterType, { value: filterValues }]) => {
      if (!filterValues) {
        return null;
      }

      if (
        filterType === 'visitExpectedAt' ||
        filterType === 'visitFinishesAt' ||
        filterType === 'visitCreatedAt'
      ) {
        const getFilterTypeTranslation = () => {
          switch (filterType) {
            case 'visitExpectedAt':
              return t('visits:filters.expectedFrom');
            case 'visitFinishesAt':
              return t('visits:filters.finishesFrom');
            case 'visitCreatedAt':
              return t('visits:filters.createdFrom');
          }
        };

        return (
          <Chip
            sx={{ mr: 1, mb: 1 }}
            color="primary"
            label={
              Array.isArray(filterValues) ? (
                <Typography variant="caption">
                  {getFilterTypeTranslation()}{' '}
                  {formatDate(
                    new Date(filterValues?.[0] as string),
                    `${dateFormats.day}-${dateFormats.month}`,
                  )}{' '}
                  {t('visits:filters.to')}{' '}
                  {formatDate(
                    new Date(filterValues?.[1] as string),
                    `${dateFormats.day}-${dateFormats.month}`,
                  )}
                </Typography>
              ) : null
            }
            size="small"
            onDelete={() => resetSingleFilter(filterType)}
          />
        );
      }
      if (Array.isArray(filterValues)) {
        return filterValues?.map((value, i) => {
          switch (filterType) {
            case 'visitMainDestinationId':
              return (
                <Chip
                  key={value.toString()}
                  sx={{ mr: 1, mb: 1 }}
                  color="primary"
                  label={
                    <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
                      {t('visits:filters.destination')}:{' '}
                      {
                        destinationsData?.resources?.find(
                          dest => dest.id.toString() === value.toString(),
                        )?.name
                      }
                    </Typography>
                  }
                  size="small"
                  onDelete={() => onDeselectFilterValue(filterType, value.toString())}
                />
              );

            case 'visitState':
              return (
                <Chip
                  key={i}
                  sx={{ mr: 1, mb: 1 }}
                  color="primary"
                  label={
                    <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
                      {t('visits:filters.visitorState')}:{' '}
                      {t(`common:statusVariants.${value as VisitState}`)}
                    </Typography>
                  }
                  size="small"
                  onDelete={() => onDeselectFilterValue(filterType, value.toString())}
                />
              );
            case 'state':
              return (
                <Chip
                  key={i}
                  sx={{ mr: 1, mb: 1 }}
                  color="primary"
                  label={
                    <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
                      {t('visits:filters.visitState')}:{' '}
                      {t(`common:statusVariantVisitor.${value as VisitorState}`)}
                    </Typography>
                  }
                  size="small"
                  onDelete={() => onDeselectFilterValue(filterType, value.toString())}
                />
              );
            case 'isHandicapped':
              return (
                <Chip
                  key={i}
                  sx={{ mr: 1, mb: 1 }}
                  color="primary"
                  label={
                    <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
                      {t('visits:filters.isGuestHandicapped')}:{' '}
                      {value === 'true' ? t('common:yes') : t('common:no')}
                    </Typography>
                  }
                  size="small"
                  onDelete={() => onDeselectFilterValue(filterType, value.toString())}
                />
              );
            case 'isPregnant':
              return (
                <Chip
                  key={i}
                  sx={{ mr: 1, mb: 1 }}
                  color="primary"
                  label={
                    <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
                      {t('visits:filters.isGuestPregnant')}:{' '}
                      {value === 'true' ? t('common:yes') : t('common:no')}
                    </Typography>
                  }
                  size="small"
                  onDelete={() => onDeselectFilterValue(filterType, value.toString())}
                />
              );
            case 'hasAccessToParking':
              return (
                <Chip
                  key={i}
                  sx={{ mr: 1, mb: 1 }}
                  color="primary"
                  label={
                    <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
                      {t('visits:filters.hasAccessToParking')}:{' '}
                      {value === 'true' ? t('common:yes') : t('common:no')}
                    </Typography>
                  }
                  size="small"
                  onDelete={() => onDeselectFilterValue(filterType, value.toString())}
                />
              );
            case 'visitCategory.id':
              return (
                <Chip
                  key={i}
                  sx={{ mr: 1, mb: 1 }}
                  color="primary"
                  label={
                    <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
                      {t('visits:filters.category')}:{' '}
                      {getTranslationBasedOnLanguage(
                        categoryData?.find(category => category.id.toString() === value?.toString())
                          ?.translations || { en: '', pl: '' },
                        i18n.language as OrganizationLanguage,
                      )}
                    </Typography>
                  }
                  size="small"
                  onDelete={() => onDeselectFilterValue(filterType, value?.toString())}
                />
              );
            case 'visitSource':
              return (
                <Chip
                  key={i}
                  sx={{ mr: 1, mb: 1 }}
                  color="primary"
                  label={
                    <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
                      {t('visits:filters.source')}: {t(`visits:source.${value as VisitSource}`)}
                    </Typography>
                  }
                  size="small"
                  onDelete={() => onDeselectFilterValue(filterType, value?.toString())}
                />
              );
            case 'visitHostConfirmed': {
              const hostConfirmedFilterValue = value.toString();

              const hostConfirmedTranslation =
                hostConfirmedFilterValue === 'true'
                  ? t('common:no')
                  : hostConfirmedFilterValue === 'false'
                    ? t('common:yes')
                    : t('visits:filters.notRequired');

              return (
                <Chip
                  key={i}
                  sx={{ mr: 1, mb: 1 }}
                  color="primary"
                  label={
                    <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
                      {t('visits:filters.requiresConfirmation')}: {hostConfirmedTranslation}
                    </Typography>
                  }
                  size="small"
                  onDelete={() => onDeselectFilterValue(filterType, hostConfirmedFilterValue)}
                />
              );
            }
            default:
              return (
                <Chip
                  key={value.toString()}
                  sx={{ mr: 1, mb: 1 }}
                  color="primary"
                  label={
                    <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
                      {filterType}: {value}
                    </Typography>
                  }
                  size="small"
                  onDelete={() => onDeselectFilterValue(filterType, value?.toString())}
                />
              );
          }
        });
      }
    });

  return (
    <>
      <Box
        sx={{
          alignItems: 'center',
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            md: '1fr auto',
            sm: 'auto',
            xs: 'auto',
          },
          justifyItems: 'flex-start',
          px: 3,
          pt: 3,
        }}>
        <Query
          searchAsType
          disabled={false}
          onChange={handleChangeQuery}
          sx={{
            minWidth: 200,
            order: {
              sm: 2,
              xs: 1,
            },
          }}
          value={query}
        />
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            order: 4,
          }}>
          <Button
            color="primary"
            disabled={disabled}
            onClick={() => {
              setOpenFilters(true);
            }}
            startIcon={<AdjustmentsIcon />}
            size="medium"
            sx={{ order: 1, ml: 2 }}
            variant={'contained'}>
            {t('visits:filters.filters')}
          </Button>
        </Box>
      </Box>
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyItems: 'flex-start',
          flexWrap: 'wrap',
          px: 3,
          pt: 2,
          pb: 1,
        }}>
        {renderFilterChips()}
      </Box>
      <VisitorsFilters
        anchor={'right'}
        open={openFilters}
        setState={setOpenFilters}
        onClose={() => setOpenFilters(false)}
        filters={filters}
        handleChangeFilterValue={handleChangeFilterValue}
        resetFilters={resetFilters}
      />
    </>
  );
};

export default VisitorsSearchAndFilters;
